import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calculator, Loader2, CheckCircle2, TrendingDown, PiggyBank, CalendarClock, Repeat } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { getVisitorId, trackEvent, getAttribution } from "../lib/track";
import { IMAGES, COMPANY } from "../lib/data";
import { ModelingDisclaimer } from "../components/site/ModelingDisclaimer";

const usd = (n) => "$" + Math.round(n).toLocaleString();

const FIELDS = [
  { key: "fleet", label: "Fleet size (mats)", min: 1 },
  { key: "years", label: "Analysis horizon (years)", min: 1 },
  { key: "deploys", label: "Deployments / mat / year", min: 1 },
  { key: "budgetPrice", label: "Budget mat price ($)", min: 1 },
  { key: "budgetLife", label: "Budget mat life (years)", min: 0.5, step: 0.5 },
  { key: "bbmPrice", label: "Big Bad Mats price ($)", min: 1 },
];

export default function ROICalculator() {
  const [v, setV] = useState({ fleet: 200, years: 10, deploys: 8, budgetPrice: 500, budgetLife: 1.5, bbmPrice: 1900 });
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const r = useMemo(() => {
    const { fleet, years, deploys, budgetPrice, budgetLife, bbmPrice } = v;
    const rebuys = Math.max(1, Math.ceil(years / Math.max(budgetLife, 0.5)));
    const budgetLifetime = rebuys * budgetPrice;
    const savePerMat = budgetLifetime - bbmPrice;
    const fleetSave = savePerMat * fleet;
    const budgetCPD = budgetPrice / Math.max(deploys * budgetLife, 1);
    const bbmCPD = bbmPrice / Math.max(deploys * years, 1);
    const breakEven = budgetLife * Math.ceil(bbmPrice / Math.max(budgetPrice, 1));
    const cpdCut = budgetCPD > 0 ? Math.round((1 - bbmCPD / budgetCPD) * 100) : 0;
    return { rebuys, budgetLifetime, savePerMat, fleetSave, budgetCPD, bbmCPD, breakEven, cpdCut,
      fleetBudget: budgetLifetime * fleet, fleetBBM: bbmPrice * fleet };
  }, [v]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val === "" ? "" : Number(val) }));

  useEffect(() => {
    trackEvent("calculator_open", { label: "Big Bad Mats — ROI Calculator", product: "Big Bad Mats" });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    setSending(true);
    const summary = `ROI CALCULATOR ANALYSIS\nFleet: ${v.fleet} mats · Horizon: ${v.years} yrs · ${v.deploys} deploys/mat/yr\n` +
      `Budget mat: ${usd(v.budgetPrice)} @ ${v.budgetLife}yr life (${r.rebuys}x re-buys = ${usd(r.budgetLifetime)}/mat)\n` +
      `Big Bad Mats: ${usd(v.bbmPrice)}/mat\n` +
      `Cost/deployment: ${usd(r.budgetCPD)} → ${usd(r.bbmCPD)} (${r.cpdCut}% lower)\n` +
      `Break-even: ~${r.breakEven} yrs · Savings/mat: ${usd(r.savePerMat)}\n` +
      `Fleet spend: budget ${usd(r.fleetBudget)} vs BBM ${usd(r.fleetBBM)} → NET SAVINGS ${usd(r.fleetSave)}`;
    try {
      await api.post("/quote", {
        name: form.name, email: form.email, company: form.company || "—",
        industry: "Ground Protection ROI Calculator", fleet_size: String(v.fleet), message: summary,
        est_value: Math.round(r.fleetBBM), products: ["Big Bad Mats"],
        visitor_id: getVisitorId(), attribution: getAttribution(),
      });
      await trackEvent("calculator_complete", { email: form.email, label: "Big Bad Mats — ROI Calculator completed", product: "Big Bad Mats" });
      setSent(true);
      toast.success("Analysis sent. Our team will follow up.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSending(false); }
  };

  const CARDS = [
    { icon: TrendingDown, v: `${r.cpdCut}%`, l: "Lower cost per deployment", flame: true },
    { icon: Repeat, v: `${r.rebuys}×`, l: "Budget-mat re-buys" },
    { icon: CalendarClock, v: `~${r.breakEven} yrs`, l: "Break-even point" },
    { icon: PiggyBank, v: usd(r.savePerMat), l: "Lifetime savings / mat", flame: true },
  ];

  return (
    <div className="min-h-screen bg-charcoal text-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to product
          </Link>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/50">
            <Calculator className="h-3.5 w-3.5 text-flame" /> Lifecycle ROI Calculator
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-2">
          <img src={IMAGES.markWhite} alt="OneHarvest" className="h-7 w-7 object-contain" />
          <span className="font-display font-extrabold tracking-tight text-sm">ONEHARVEST</span>
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">Big Bad Mats Lifecycle ROI Calculator</h1>
        <p className="mt-3 text-white/60 max-w-2xl">Model total cost of ownership against budget mats. Adjust the inputs — the numbers update live. Email yourself the full analysis.</p>

        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          {/* inputs */}
          <div className="lg:col-span-2 bg-ink border border-white/10 p-6" data-testid="roi-inputs">
            <h2 className="font-display font-bold text-lg mb-4">Your Assumptions</h2>
            <div className="space-y-4">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-[11px] uppercase tracking-widest text-white/50 mb-1">{f.label}</label>
                  <input
                    type="number" min={f.min} step={f.step || 1} value={v[f.key]}
                    data-testid={`input-${f.key}`}
                    onChange={(e) => set(f.key, e.target.value)}
                    className="w-full bg-charcoal border border-white/15 px-3 py-2.5 font-mono text-white focus:border-flame focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* results */}
          <div className="lg:col-span-3 space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="roi-results">
              {CARDS.map((c) => (
                <div key={c.l} className="bg-ink border border-white/10 p-4">
                  <c.icon className={`h-5 w-5 ${c.flame ? "text-flame" : "text-white/60"}`} />
                  <div className={`mt-3 font-display font-extrabold text-2xl ${c.flame ? "text-flame" : "text-white"}`} data-testid={`result-${c.l.toLowerCase().replace(/\W+/g, "-")}`}>{c.v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">{c.l}</div>
                </div>
              ))}
            </div>

            {/* fleet savings headline */}
            <div className="bg-ink border border-white/10 p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">Fleet impact · {v.fleet} mats · {v.years}-yr horizon</div>
              <div className="mt-4 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                <div className="bg-ink p-4">
                  <div className="text-[10px] uppercase tracking-wider text-white/50">Budget spend</div>
                  <div className="mt-2 font-display font-extrabold text-2xl text-white/85">{usd(r.fleetBudget)}</div>
                </div>
                <div className="bg-ink p-4">
                  <div className="text-[10px] uppercase tracking-wider text-white/50">Big Bad spend</div>
                  <div className="mt-2 font-display font-extrabold text-2xl text-white">{usd(r.fleetBBM)}</div>
                </div>
                <div className="bg-flame p-4">
                  <div className="text-[10px] uppercase tracking-wider text-white/80">Net savings</div>
                  <div className="mt-2 font-display font-extrabold text-2xl text-white" data-testid="result-fleet-savings">{usd(r.fleetSave)}</div>
                </div>
              </div>
            </div>

            {/* lead capture */}
            <div className="bg-ink border border-flame/40 p-6">
              {sent ? (
                <div className="flex items-center gap-3 text-flame" data-testid="roi-sent">
                  <CheckCircle2 className="h-6 w-6" />
                  <div>
                    <div className="font-display font-bold text-white">Analysis sent to your team.</div>
                    <div className="text-sm text-white/60">We'll follow up within one business day with a tailored model.</div>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="roi-lead-form">
                  <h3 className="font-display font-bold text-lg">Email me this analysis</h3>
                  <p className="text-sm text-white/50 mt-1">Get the full breakdown and a tailored, project-specific model.</p>
                  <div className="mt-4 grid sm:grid-cols-3 gap-3">
                    <input placeholder="Full name" value={form.name} data-testid="lead-name" onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-charcoal border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none" />
                    <input type="email" placeholder="Work email" value={form.email} data-testid="lead-email" onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-charcoal border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none" />
                    <input placeholder="Company" value={form.company} data-testid="lead-company" onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-charcoal border border-white/15 px-3 py-2.5 text-white focus:border-flame focus:outline-none" />
                  </div>
                  <button type="submit" disabled={sending} data-testid="roi-submit" className="mt-4 inline-flex items-center gap-2 bg-flame text-white px-6 py-3 font-semibold hover:bg-flame/90 transition-colors disabled:opacity-60">
                    {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Send my ROI analysis"}
                  </button>
                </form>
              )}
            </div>
            <ModelingDisclaimer dark testid="roi-disclaimer" />
          </div>
        </div>
      </div>
    </div>
  );
}
